/**
 * Typed hooks for every Settings GET endpoint.
 *
 * Each is a thin wrapper around `useApiQuery` so the Settings page can do:
 *
 *   const { data: employees, isLoading } = useSettingsEmployees();
 *
 * and get a strongly-typed `SettingsEmployee[]` back. No React Query yet —
 * see the comment in useApiQuery.ts for the rationale.
 */
import { api } from './api';
import { useApiQuery } from './useApiQuery';
import type { RateComparisonKind } from '@coffman-tm/shared';

export const useSettingsProject = () =>
  useApiQuery((t, signal) => api.settings.project(t, { signal }));
export const useSettingsEmployees = () =>
  useApiQuery((t, signal) => api.settings.employees(t, { signal }));
export const useSettingsEquipment = () =>
  useApiQuery((t, signal) => api.settings.equipment(t, { signal }));
export const useSettingsMaterials = () =>
  useApiQuery((t, signal) => api.settings.materials(t, { signal }));
export const useSettingsPcos = () =>
  useApiQuery((t, signal) => api.settings.pcos(t, { signal }));
export const useSettingsActivityCodes = () =>
  useApiQuery((t, signal) => api.settings.activityCodes(t, { signal }));
export const useSettingsUnknowns = () =>
  useApiQuery((t, signal) => api.settings.unknowns(t, { signal }));
export const useSettingsForemanPlaceholders = () =>
  useApiQuery((t, signal) => api.settings.foremanPlaceholders(t, { signal }));

/**
 * Rates for one roster kind across every job the caller is on.
 *
 * Mount this only while the Compare rates modal is open — it is a cross-job
 * read nobody asked for until they click the button, and `kind` is a dep so
 * switching kinds refetches rather than showing the other list's rows.
 */
export const useRateComparison = (kind: RateComparisonKind) =>
  useApiQuery((t, signal) => api.settings.rateComparison(t, kind, { signal }), [kind]);

/**
 * The Users tab's list. NOT a settings endpoint — a user spans projects, so it
 * lives at `/api/users` — but it is fetched from the Settings page, so the hook
 * sits with its siblings.
 *
 * Admin-only server-side. Mount it behind the same `isAdmin` check the tab body
 * uses, or a PM's page load spends a request earning a 403.
 */
export const useAdminUsers = () => useApiQuery((t, signal) => api.users.list(t, { signal }));
